import { useState, useCallback } from 'react';
import { Upload, X, File, Image as ImageIcon, FileText } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useDropzone } from 'react-dropzone';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { toast } from '@/hooks/use-toast';

interface SupportFileUploadProps {
  threadId: string;
  messageId?: string;
  onUploadComplete: (attachment: { name: string; url: string; size: number; type: string }) => void;
  onUploadError?: (error: string) => void;
}

export function SupportFileUpload({ threadId, messageId, onUploadComplete, onUploadError }: SupportFileUploadProps) {
  const { user } = useAuth();
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);

  const onDrop = useCallback(async (acceptedFiles: File[]) => {
    if (!user || acceptedFiles.length === 0) return;

    setUploading(true);
    setUploadProgress(0);

    try {
      for (const file of acceptedFiles) {
        // Validate file size (10MB max)
        if (file.size > 10485760) {
          throw new Error(`File "${file.name}" is too large. Maximum size is 10MB.`);
        }

        // Generate unique file path: userId/threadId/messageId/filename
        const fileExt = file.name.split('.').pop();
        const fileName = `${Date.now()}-${file.name}`;
        const filePath = `${user.id}/${threadId}/${messageId || 'temp'}/${fileName}`;

        // Upload to storage
        const { data, error } = await supabase.storage
          .from('support-attachments')
          .upload(filePath, file, {
            cacheControl: '3600',
            upsert: false,
          });

        if (error) throw error;

        // Get signed URL (valid for 1 year since bucket is private)
        const { data: signedUrlData, error: signedUrlError } = await supabase.storage
          .from('support-attachments')
          .createSignedUrl(filePath, 31536000); // 1 year in seconds

        if (signedUrlError) throw signedUrlError;

        // Return attachment info
        onUploadComplete({
          name: file.name,
          url: signedUrlData.signedUrl,
          size: file.size,
          type: file.type,
        });

        setUploadProgress(100);
      }

      toast({
        title: 'Files uploaded',
        description: `${acceptedFiles.length} file(s) uploaded successfully`,
      });
    } catch (error: any) {
      console.error('Upload error:', error);
      const errorMessage = error.message || 'Failed to upload file';
      toast({
        title: 'Upload failed',
        description: errorMessage,
        variant: 'destructive',
      });
      onUploadError?.(errorMessage);
    } finally {
      setUploading(false);
      setUploadProgress(0);
    }
  }, [user, threadId, messageId, onUploadComplete, onUploadError]);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    maxFiles: 5,
    maxSize: 10485760, // 10MB
    accept: {
      'image/*': ['.jpg', '.jpeg', '.png', '.gif', '.webp'],
      'application/pdf': ['.pdf'],
      'application/msword': ['.doc'],
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document': ['.docx'],
      'text/plain': ['.txt'],
      'text/csv': ['.csv'],
      'application/zip': ['.zip'],
    },
  });

  return (
    <div
      {...getRootProps()}
      className={`border-2 border-dashed rounded-lg p-4 transition-colors cursor-pointer ${
        isDragActive 
          ? 'border-primary bg-primary/5' 
          : 'border-border hover:border-primary/50'
      }`}
    >
      <input {...getInputProps()} />
      
      <div className="flex flex-col items-center gap-2 text-center">
        <Upload className={`h-6 w-6 ${isDragActive ? 'text-primary' : 'text-muted-foreground'}`} />
        
        {uploading ? (
          <>
            <p className="text-sm font-medium">Uploading...</p>
            <div className="w-full max-w-xs h-2 bg-muted rounded-full overflow-hidden">
              <div 
                className="h-full bg-primary transition-all duration-300"
                style={{ width: `${uploadProgress}%` }}
              />
            </div>
          </>
        ) : (
          <>
            <p className="text-sm font-medium">
              {isDragActive ? 'Drop files here' : 'Drag & drop files here'}
            </p>
            <p className="text-xs text-muted-foreground">
              or click to browse (max 10MB per file)
            </p>
            <p className="text-xs text-muted-foreground">
              Supported: Images, PDF, Word, Text, CSV, ZIP
            </p>
          </>
        )}
      </div>
    </div>
  );
}

interface AttachmentPreviewProps {
  attachment: { name: string; url: string; size: number; type: string };
  onRemove?: () => void;
  showRemove?: boolean;
}

export function AttachmentPreview({ attachment, onRemove, showRemove = false }: AttachmentPreviewProps) {
  const formatFileSize = (bytes: number) => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1048576) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / 1048576).toFixed(1)} MB`;
  };

  const getFileIcon = () => {
    if (attachment.type.startsWith('image/')) return <ImageIcon className="h-4 w-4" />;
    if (attachment.type === 'application/pdf') return <FileText className="h-4 w-4" />;
    return <File className="h-4 w-4" />;
  };

  const isImage = attachment.type.startsWith('image/');

  return (
    <div className="relative group">
      {isImage ? (
        <div className="relative">
          <img
            src={attachment.url}
            alt={attachment.name}
            className="max-w-xs max-h-48 rounded-lg object-cover"
          />
          {showRemove && onRemove && (
            <Button
              variant="destructive"
              size="icon"
              className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity"
              onClick={onRemove}
            >
              <X className="h-4 w-4" />
            </Button>
          )}
        </div>
      ) : (
        <a 
          href={attachment.url} 
          target="_blank" 
          rel="noopener noreferrer"
          className="flex items-center gap-3 p-3 border rounded-lg bg-muted/50 hover:bg-muted transition-colors cursor-pointer group"
        >
          {getFileIcon()}
          <div className="flex-1 min-w-0">
            <p className="text-sm font-medium truncate group-hover:text-primary transition-colors">{attachment.name}</p>
            <p className="text-xs text-muted-foreground">{formatFileSize(attachment.size)}</p>
          </div>
          {showRemove && onRemove && (
            <Button 
              variant="ghost" 
              size="icon" 
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                onRemove();
              }}
            >
              <X className="h-4 w-4" />
            </Button>
          )}
        </a>
      )}
    </div>
  );
}